import { COUNTRY_OPTIONS, countryLabel } from "./countries";
import CityInput from "./CityInput";
import React, { useState } from "react";
import { BackButton, Button, Card, Chip, Input, SectionTitle, ShieldIcon } from "./ui";
import PushToggle from "./PushToggle";

/* -------------------------------------------------------------------------- */
/* Profile — owner profile page                                               */
/* -------------------------------------------------------------------------- */

export default function Profile({ user, dogs, onBack, onLogout, onUpdateProfile, onGoMyDogs, onGoMyActivities, onGoActivities, onGoSos, onGoVetEmergency, isAdmin, onGoAdmin }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    name: user.name || "",
    city: user.city || "",
    countryCode: user.countryCode || "PA",
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    setForm({
      name: user.name || "",
      city: user.city || "",
      countryCode: user.countryCode || "PA",
    });
    setError("");
    setEditing(true);
  };

  const save = async (ev) => {
    ev.preventDefault();
    setError("");
    const name = form.name.trim();
    if (name.length < 2) {
      setError("El nombre debe tener al menos 2 caracteres.");
      return;
    }
    if (!form.city.trim()) {
      setError("Selecciona o escribe tu ciudad.");
      return;
    }
    setSaving(true);
    try {
      await onUpdateProfile({
        name,
        city: form.city.trim(),
        country_code: form.countryCode,
      });
      setEditing(false);
    } catch (err) {
      setError(err.message || "No pudimos actualizar tu perfil. Intenta de nuevo.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 pb-28 pt-4 sm:px-6" data-testid="profile-screen">
      <div className="mb-4">
        <BackButton onClick={onBack} testId="profile-back-btn" />
      </div>

      <SectionTitle eyebrow="Mi cuenta">Mi perfil</SectionTitle>

      <Card className="mt-5 overflow-hidden">
        <div
          className="relative p-6 text-white"
          style={{
            background:
              "linear-gradient(135deg, #0B2D4D 0%, #153d63 60%, #1f4d7d 100%)",
          }}
        >
          <div className="pc-paws absolute inset-0 opacity-30" aria-hidden="true" />
          <div className="relative flex items-center gap-4">
            <div
              className="flex h-20 w-20 items-center justify-center rounded-3xl text-4xl font-black"
              style={{ background: "#67D5B5", color: "#0B2D4D" }}
              aria-hidden="true"
            >
              {(user.name || "🐾")
                .split(" ")
                .map((w) => w[0])
                .slice(0, 2)
                .join("")}
            </div>
            <div>
              <p className="font-display text-2xl font-black" data-testid="profile-name">
                {user.name}
              </p>
              <p className="text-sm text-emerald-100/90">📍 {user.city || "Sin ubicación"}</p>
              <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-white/10 px-3 py-1 text-xs font-bold">
                <ShieldIcon size={13} className="text-[#67D5B5]" />
                {user.emailVerified ? "Correo verificado" : "Pendiente de verificar"}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 divide-x divide-slate-100 border-t border-slate-100 text-center">
          <Stat label="Peluditos" value={dogs.length} />
          <Stat label="Solicitudes" value={user.stats?.requests ?? 0} />
          <Stat label="Encuentros" value={user.stats?.meetings ?? 0} />
        </div>
      </Card>

      {/* Owner details / edit */}
      <Card className="mt-6 p-5">
        {!editing ? (
          <div data-testid="profile-details">
            <div className="mb-4 flex items-center justify-between">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FF5C5C]">
                Datos del dueño
              </p>
              <Button
                variant="outline"
                onClick={startEdit}
                testId="profile-edit-btn"
                className="px-4 py-2 text-sm"
              >
                Editar perfil
              </Button>
            </div>
            <DetailRow label="Nombre" value={user.name} testId="detail-name" />
            <DetailRow label="Correo" value={user.email} testId="detail-email" />
            <DetailRow
              label="Estado"
              value={user.emailVerified ? "Correo verificado ✅" : "Pendiente de verificar ⏳"}
              testId="detail-status"
            />
            <DetailRow
              label="País de PawCita"
              value={countryLabel(user.countryCode)}
              testId="detail-country"
            />
            <DetailRow label="Ciudad" value={user.city || "—"} testId="detail-city" last />
          </div>
        ) : (
          <form onSubmit={save} className="space-y-4" data-testid="profile-edit-form" noValidate>
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FF5C5C]">
              Editar perfil
            </p>
            <Input
              label="Nombre"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Tu nombre"
              maxLength={80}
              testId="profile-name-input"
            />
            <label className="block">
              <span className="mb-1.5 block text-sm font-bold text-[#0B2D4D]">
                País de PawCita
              </span>
              <select
                value={form.countryCode}
                onChange={(e) =>
                  setForm({ ...form, countryCode: e.target.value, city: "" })
                }
                data-testid="profile-country-select"
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-[#0B2D4D] outline-none focus:border-[#67D5B5]"
              >
                {COUNTRY_OPTIONS.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
              </select>
            </label>
            <CityInput
              countryCode={form.countryCode}
              value={form.city}
              onChange={(value) => setForm({ ...form, city: value })}
              label="Ciudad"
              testId="profile-city-input"
            />
            <div className="rounded-2xl bg-slate-50 p-3 text-xs text-slate-500">
              El correo, el estado de verificación y tu contraseña no se editan aquí.
            </div>
            {error && (
              <p className="text-sm font-semibold text-rose-600" data-testid="profile-edit-error">
                {error}
              </p>
            )}
            <div className="flex gap-3">
              <Button
                variant="primary"
                type="submit"
                testId="profile-save-btn"
                disabled={saving}
                className="flex-1 py-3"
              >
                {saving ? "Guardando…" : "Guardar cambios"}
              </Button>
              <Button
                variant="outline"
                type="button"
                onClick={() => setEditing(false)}
                testId="profile-cancel-btn"
                className="flex-1 py-3"
              >
                Cancelar
              </Button>
            </div>
          </form>
        )}
      </Card>

      {/* Mis perros — tarjeta destacada (Fase 8B: acceso desde Perfil) */}
      <button
        type="button"
        onClick={onGoMyDogs}
        data-testid="profile-mydogs-card"
        className="mt-6 flex w-full items-center gap-4 rounded-3xl bg-gradient-to-br from-[#FF5C5C] to-[#ff7a7a] p-5 text-left text-white shadow-[0_12px_30px_-12px_rgba(255,92,92,0.7)] transition active:translate-y-[1px]"
      >
        <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-white/20 text-3xl">🐶</span>
        <span className="min-w-0 flex-1">
          <span className="block text-lg font-black">Mis perros</span>
          <span className="block text-sm text-white/90">Agrega, edita o revisa los perfiles de tus peluditos.</span>
        </span>
        <span className="flex-shrink-0 text-2xl">›</span>
      </button>

      {/* Mis Actividades — tarjeta destacada (Fase 8E) */}
      <button
        type="button"
        onClick={onGoMyActivities}
        data-testid="profile-myactivities-card"
        className="mt-3 flex w-full items-center gap-4 rounded-3xl bg-gradient-to-br from-[#0B2D4D] to-[#153d63] p-5 text-left text-white shadow-[0_12px_30px_-12px_rgba(11,45,77,0.7)] transition active:translate-y-[1px]"
      >
        <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-white/15 text-3xl">📅</span>
        <span className="min-w-0 flex-1">
          <span className="block text-lg font-black">Mis Actividades</span>
          <span className="block text-sm text-white/85">Consulta las actividades a las que te uniste con tu peludito.</span>
        </span>
        <span className="flex-shrink-0 text-2xl">›</span>
      </button>

      {/* PawCita SOS — acceso desde Perfil (rojo/coral suave, no alarmista) */}
      <button
        type="button"
        onClick={onGoSos}
        data-testid="profile-sos-card"
        className="mt-3 flex w-full items-center gap-4 rounded-3xl bg-[#E11D48] p-5 text-left text-white shadow-[0_12px_30px_-12px_rgba(244,63,94,0.55)] transition active:translate-y-[1px]"
      >
        <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-white/20 text-3xl">🆘</span>
        <span className="min-w-0 flex-1">
          <span className="block text-lg font-black">PawCita SOS</span>
          <span className="block text-sm text-white/90">Activa o revisa alertas si tu peludito se pierde.</span>
        </span>
        <span className="flex-shrink-0 text-2xl">›</span>
      </button>

      {/* 🚑 Emergencia veterinaria — herramienta de seguridad (cerca de SOS) */}
      <button
        type="button"
        onClick={onGoVetEmergency}
        data-testid="profile-vet-emergency-card"
        className="mt-3 flex w-full items-center gap-4 rounded-3xl bg-[#0B2D4D] p-5 text-left text-white shadow-[0_12px_30px_-12px_rgba(11,45,77,0.55)] transition active:translate-y-[1px]"
      >
        <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-white/15 text-3xl">🚑</span>
        <span className="min-w-0 flex-1">
          <span className="block text-lg font-black">Emergencia veterinaria</span>
          <span className="block text-sm text-white/90">Encuentra atención veterinaria cerca de ti.</span>
        </span>
        <span className="flex-shrink-0 text-2xl">›</span>
      </button>

      <PushToggle />
      <div className="mt-6">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FF5C5C]">
            Mis peluditos
          </p>
          <button
            onClick={onGoMyDogs}
            className="text-sm font-bold text-[#0B2D4D] underline-offset-4 hover:underline"
            data-testid="profile-manage-dogs-btn"
          >
            Administrar
          </button>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {dogs.map((d) => (
            <Card key={d.id} className="flex gap-3 p-3">
              <img
                src={d.image}
                alt={d.name}
                className="h-16 w-16 rounded-2xl object-cover"
              />
              <div className="min-w-0 flex-1">
                <p className="font-display text-lg font-black text-[#0B2D4D]">{d.name}</p>
                <p className="text-sm text-slate-500">
                  {d.breed} · {d.age} años · {d.sex}
                </p>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  <Chip tone={d.vaccines ? "mint" : "yellow"}>
                    {d.vaccines ? "Vacunas ✓" : "Vacunas pendientes"}
                  </Chip>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Responsibility cards */}
      <div className="mt-6 space-y-3">
        <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FF5C5C]">
          PawCita responsable
        </p>
        <TrustRow
          emoji="🌿"
          title="Encuentros seguros"
          text="Primer encuentro recomendado en lugares públicos y con correa."
        />
        <TrustRow
          emoji="🩺"
          title="Salud responsable"
          text="Verifica vacunas y consulta con tu veterinario antes del cruce."
        />
        <TrustRow
          emoji="🔒"
          title="Comunicación protegida"
          text="No compartas teléfonos, correos ni redes sociales dentro del chat."
        />
        <TrustRow
          emoji="🐾"
          title="Comunidad PawCita"
          text="Promovemos encuentros responsables entre mascotas."
        />
      </div>

      {isAdmin && (
        <div className="mt-6">
          <Button
            variant="primary"
            onClick={onGoAdmin}
            testId="profile-admin-btn"
            className="w-full py-4"
          >
            🛠️ Panel de administración
          </Button>
        </div>
      )}

      {onLogout && (
        <div className="mt-8">
          <Button
            variant="outline"
            onClick={onLogout}
            testId="profile-logout-btn"
            className="w-full py-4"
          >
            Cerrar sesión
          </Button>
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value, testId, last }) {
  return (
    <div className={`flex items-center justify-between py-2.5 ${last ? "" : "border-b border-slate-100"}`}>
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-sm font-bold text-[#0B2D4D]" data-testid={testId}>{value}</span>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="py-4">
      <p className="font-display text-2xl font-black text-[#0B2D4D]">{value}</p>
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
        {label}
      </p>
    </div>
  );
}

function TrustRow({ emoji, title, text }) {
  return (
    <Card className="flex items-start gap-3 p-4">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-xl">
        {emoji}
      </span>
      <div>
        <p className="font-display text-lg font-black text-[#0B2D4D]">{title}</p>
        <p className="mt-1 text-sm leading-relaxed text-slate-500">{text}</p>
      </div>
    </Card>
  );
}
